import React from 'react';

import BulkCreateBoxesFlow from 'bulk-import/BulkCreateBoxesFlow';
import BulkCreateContainersFlow from 'bulk-import/BulkCreateContainersFlow';
import BulkCreateLocationsFlow from 'bulk-import/BulkCreateLocationsFlow';
import BulkCreatePlatesFlow from 'bulk-import/BulkCreatePlatesFlow';
import BulkTransferStorablesFlow from 'bulk-import/BulkTransferStorablesFlow';
import BulkUpdateBoxesFlow from 'bulk-import/BulkUpdateBoxesFlow';
import BulkUpdateContainersFlow from 'bulk-import/BulkUpdateContainersFlow';
import {openBulkUpdateLocationsFlow} from 'bulk-import/BulkUpdateLocationsFlow';
import BulkUpdatePlatesFlow from 'bulk-import/BulkUpdatePlatesFlow';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {NavStoreRegistry} from 'constants/RegistryConstants';
import {LocationSummary, TransferFlow} from 'constants/SampleConstants';
import * as CreateContainerModal from 'samples/containers/CreateContainerModal';
import CreateBoxModal from 'samples/CreateBoxModal';
import CreateLocationModal from 'samples/CreateLocationModal';
import CreatePlateModal from 'samples/CreatePlateModal';
import {openTransferModal} from 'samples/transfers/TransferModal';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

interface Props {
  registry: NavStoreRegistry;
  isExpanded: boolean;
  listedLocation: LocationSummary | null;
}

function StorableCreateMenu(props: Props): JSX.Element {
  const ACTION_ORIGIN = ActionOrigin.INVENTORY_SIDEPANEL_CREATE;

  function handleOpenCreateBox(): void {
    CreateBoxModal.open({
      initialRegistry: props.registry,
      initialParentStorable: props.listedLocation,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  function handleOpenCreatePlate(): void {
    CreatePlateModal.open({
      initialRegistry: props.registry,
      initialParentStorable: props.listedLocation,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  function handleOpenCreateLocation(): void {
    CreateLocationModal.open({
      initialRegistry: props.registry,
      initialParentStorable: props.listedLocation,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  function handleOpenCreateContainer(): void {
    CreateContainerModal.open({
      initialRegistry: props.registry,
      initialParentStorable: props.listedLocation,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  function handleOpenBulkCreateLocations(): void {
    BulkCreateLocationsFlow.open({
      initialRegistry: props.registry,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  function handleOpenBulkUpdateLocations(): void {
    openBulkUpdateLocationsFlow({
      initialRegistry: props.registry,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  function handleOpenBulkCreateBoxes(): void {
    BulkCreateBoxesFlow.open({
      initialRegistry: props.registry,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  function handleOpenBulkUpdateBoxes(): void {
    BulkUpdateBoxesFlow.open({
      initialRegistry: props.registry,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  function handleOpenBulkCreatePlates(): void {
    BulkCreatePlatesFlow.open({
      initialRegistry: props.registry,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  function handleOpenBulkUpdatePlates(): void {
    BulkUpdatePlatesFlow.open({
      initialRegistry: props.registry,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  function handleOpenBulkCreateContainers(): void {
    BulkCreateContainersFlow.open({
      initialRegistry: props.registry,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  function handleOpenBulkUpdateContainers(): void {
    BulkUpdateContainersFlow.open({
      initialRegistry: props.registry,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  function handleOpenBulkTransferStorables(): void {
    BulkTransferStorablesFlow.open();
  }

  function handleOpenTransferModal(): void {
    openTransferModal({
      flow: TransferFlow.TRANSFER_GLOBAL,
      registryId: props.registry?.id,
      actionOrigin: ACTION_ORIGIN,
    });
  }

  const menuProps = {
    width: Menu.WIDTHS.MEDIUM,
    height: Menu.HEIGHTS.MEDIUM,
    dropLeft: props.isExpanded,
  };
  const includeCreateLocationsOptions =
    props.registry && props.registry.permissions && props.registry.permissions.locationsCreatable;
  const menu = (
    <Menu {...menuProps}>
      <Section>
        <Option icon={ModelTypeConstants.LOCATION}>
          Location
          <Menu dropLeft={menuProps.dropLeft}>
            {includeCreateLocationsOptions && (
              <Option onSelect={handleOpenCreateLocation}>Create location</Option>
            )}
            {includeCreateLocationsOptions && (
              <Option onSelect={handleOpenBulkCreateLocations}>Import locations</Option>
            )}
            <Option onSelect={handleOpenBulkUpdateLocations}>Update locations</Option>
          </Menu>
        </Option>
        <Option icon={ModelTypeConstants.BOX}>
          Box
          <Menu dropLeft={menuProps.dropLeft}>
            <Option onSelect={handleOpenCreateBox}>Create box</Option>
            <Option onSelect={handleOpenBulkCreateBoxes}>Import boxes</Option>
            <Option onSelect={handleOpenBulkUpdateBoxes}>Update boxes</Option>
          </Menu>
        </Option>
        <Option icon={ModelTypeConstants.PLATE}>
          Plate
          <Menu dropLeft={menuProps.dropLeft}>
            <Option onSelect={handleOpenCreatePlate}>Create plate</Option>
            <Option onSelect={handleOpenBulkCreatePlates}>Import plates</Option>
            <Option onSelect={handleOpenBulkUpdatePlates}>Update plates</Option>
          </Menu>
        </Option>
        <Option icon={ModelTypeConstants.CONTAINER}>
          Container
          <Menu dropLeft={menuProps.dropLeft}>
            <Option onSelect={handleOpenCreateContainer}>Create containers</Option>
            <Option onSelect={handleOpenBulkCreateContainers}>Import containers</Option>
            <Option onSelect={handleOpenBulkUpdateContainers}>Update containers</Option>
          </Menu>
        </Option>
      </Section>
      {props.registry && (
        <Option icon={ModelTypeConstants.SAMPLE} onSelect={handleOpenTransferModal}>
          Transfer samples
        </Option>
      )}
      <Option icon="tube" onSelect={handleOpenBulkTransferStorables}>
        Import transfer data
      </Option>
    </Menu>
  );
  return (
    <div>
      <Dropdown menu={menu}>
        <div>
          <Tooltip title="Create..." placement={TooltipPlacement.BOTTOM} container="body">
            <Button
              buttonStyle={props.isExpanded ? ButtonStyle.SECONDARY : ButtonStyle.TERTIARY_BLUE}
              disabled={!props.registry}
              icon="compose"
              size={ButtonSize.SMALL}
              text={props.isExpanded ? 'Create' : null}
            />
          </Tooltip>
        </div>
      </Dropdown>
    </div>
  );
}

export default StorableCreateMenu;
